import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  IconButton,
  Typography,
} from '@mui/material';
import { CustomDataTableProps } from "../customDataTable/customDataTableProps";
import { KeyboardArrowDown, KeyboardArrowRight } from '@mui/icons-material';
import CustomDataTableStyles from '../../assets/css/customDataTable.styles';
import { CustomTableColumnsProps } from '../customDataTable/customTableColumnsProps';

interface DataRow {
  [key: string]: any;
  lookupValues?: DataRow[];
}

const CustomCollapseDataTable: React.FC<CustomDataTableProps> = ({
  columns,
  childrenColumns,
  data = [],
  count,
  rowsPerPageOptions = [5, 10, 25, 100],
  isColored,
  onPageChange,
  onRowsPerPageChange,
}) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[0]);
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
    if (onPageChange) {
      onPageChange(newPage);
    }
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    if (onRowsPerPageChange) {
      onRowsPerPageChange(event);
    }
  };

  const handleRowClick = (index: number, isParent: boolean) => {
    if (isParent) {
      const newExpandedRows = new Set(expandedRows);
      if (expandedRows.has(index)) {
        newExpandedRows.delete(index);
      } else {
        newExpandedRows.add(index);
      }
      setExpandedRows(newExpandedRows);
    }
  };

  const currentData = data.slice(page * rowsPerPage, (page + 1) * rowsPerPage) as DataRow[];

  return (
    <Paper sx={CustomDataTableStyles.paper}>
      <TableContainer sx={CustomDataTableStyles.tableContainer}>
        <Table>
          <TableHead>
            <TableRow>
              {columns.filter(col => col.visible !== false).map((column: CustomTableColumnsProps) => (
                <TableCell
                  key={column.id}
                  align={column.align}
                  sx={CustomDataTableStyles.tableHeadCell}
                >
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
          {currentData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} align="center">
                  <Typography variant="body1">No data available</Typography>
                </TableCell>
              </TableRow>
            ) : (
            currentData.map((row, rowIndex) => (
              <React.Fragment key={rowIndex}>
                <TableRow>
                  {columns.filter(col => col.visible !== false).map((column: CustomTableColumnsProps, colIndex) => (
                    <TableCell
                      key={column.id}
                      align={column.align}
                      sx={{
                        ...(colIndex === 0 ? { cursor: 'pointer' } : {}),
                        ...(expandedRows.has(rowIndex) ? { fontWeight: 'bold' } : {}),
                      }}
                      onClick={() => handleRowClick(rowIndex, colIndex === 0)}
                    >
                      {row.lookupValues && row.lookupValues.length > 0 && colIndex === 0 ? (
                        <IconButton size="small">
                          {expandedRows.has(rowIndex) ? (
                            <KeyboardArrowDown />
                          ) : (
                            <KeyboardArrowRight />
                          )}
                        </IconButton>
                      ) : null}
                      {column.renderCell ? column.renderCell({ row }) : row[column.id]}
                    </TableCell>
                  ))}
                </TableRow>
                {row.lookupValues && childrenColumns && row.lookupValues.length > 0 &&
                  expandedRows.has(rowIndex) && (
                    <>
                      {row.lookupValues.map((child: any, childIndex: number) => (
                        <TableRow key={childIndex} sx={CustomDataTableStyles.childRow}>
                          {childrenColumns.filter(col => col.visible !== false).map((column: CustomTableColumnsProps, columnIndex) => (
                            <TableCell
                              key={columnIndex}
                              align={column.align}
                              sx={[
                                ...(columnIndex === 0 ? [{ pl: 8 }] : []),
                                CustomDataTableStyles.childCell
                              ]}
                            >
                              {column.renderCell ? column.renderCell({ row: child }) : child[column.id]}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </>
                  )}
              </React.Fragment>
            ))
          )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={rowsPerPageOptions}
        component="div"
        count={count}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        sx={CustomDataTableStyles.tablePagination}
      />
    </Paper>
  );
};

export default CustomCollapseDataTable;
